/**
 * Reine Anzeige-Logik für Clients des Konfigurator-Vertrags – das Dashboard
 * nutzt sie direkt, das ERP übernimmt sie wörtlich (siehe ERP-Plan).
 */
/**
 * Schritte, die ein Client zeigt: mit mindestens einem sichtbaren Feld oder
 * einem vorhandenen Baustein, gekürzt auf das Sichtbare. Sichtbare Felder ohne
 * Schritt landen in „Weitere Angaben“ vor dem letzten Schritt.
 */
export function sichtbareSchritte(schritte, sichtbareFelder, vorschau) {
    const felder = new Set(sichtbareFelder);
    const bausteine = new Set(vorschau.map((b) => b.schluessel));
    const zugeordnet = new Set(schritte.flatMap((s) => s.felder));
    const ergebnis = schritte
        .map((s) => ({
        ...s,
        felder: s.felder.filter((f) => felder.has(f)),
        vorschau: s.vorschau.filter((v) => bausteine.has(v)),
    }))
        .filter((s) => s.felder.length > 0 || s.vorschau.length > 0);
    const uebrig = sichtbareFelder.filter((f) => !zugeordnet.has(f));
    if (uebrig.length > 0) {
        const weitere = { schluessel: "weitere", titel: "Weitere Angaben", hinweis: null, felder: uebrig, vorschau: [] };
        ergebnis.splice(Math.max(ergebnis.length - 1, 0), 0, weitere);
    }
    return ergebnis;
}
const teile = (wert, filter) => {
    if (wert === null || wert === undefined || wert === "")
        return [];
    const text = String(wert);
    return filter.art === "liste" ? text.split(",").map((t) => t.trim()).filter(Boolean) : [text];
};
/** Vorkommende Werte eines Auswahl- oder Listenfilters, alphabetisch. */
export function filterWerte(optionen, filter) {
    const werte = new Set();
    for (const o of optionen)
        for (const t of teile(o.merkmale?.[filter.schluessel], filter))
            werte.add(t);
    return [...werte].sort((a, b) => a.localeCompare(b, "de", { sensitivity: "base" }));
}
/**
 * Optionen, die alle gesetzten Filter erfüllen. `eingaben` je Filterschlüssel;
 * leer heißt „Filter aus“. Groß/klein zählt nirgends.
 */
export function filtereOptionen(optionen, tabelle, eingaben) {
    const textSpalten = tabelle.spalten.filter((s) => s.schluessel !== "preis").map((s) => s.schluessel);
    return optionen.filter((o) => tabelle.filter.every((f) => {
        const eingabe = (eingaben[f.schluessel] ?? "").trim().toLowerCase();
        if (!eingabe)
            return true;
        if (f.art === "suche") {
            const text = [o.label, ...textSpalten.map((k) => o.merkmale?.[k])]
                .filter((w) => w !== null && w !== undefined)
                .join(" ")
                .toLowerCase();
            return eingabe.split(/\s+/).every((wort) => text.includes(wort));
        }
        return teile(o.merkmale?.[f.schluessel], f).some((t) => t.toLowerCase() === eingabe);
    }));
}
const summe = (werte) => werte.reduce((a, b) => a + b, 0);
const runde2 = (wert) => Math.round((wert + Number.EPSILON) * 100) / 100;
/**
 * Ziehbalken: setzt Grenze `index` (nach Segment `index`, 0-basiert) auf
 * `position` – gemessen vom linken Rand in der Einheit der Segmente. Das linke
 * Segment rastet auf `raster` ein und hält `min`; die Segmente dazwischen
 * bleiben, das letzte ist der Rest und behält mindestens `min`.
 */
export function zieheGrenze(werte, index, position, raster, min) {
    const n = werte.length;
    if (index < 0 || index >= n - 1)
        return werte.slice();
    const gesamt = summe(werte);
    const vorher = summe(werte.slice(0, index));
    const zwischen = summe(werte.slice(index + 1, n - 1));
    const maxBreite = Math.floor((gesamt - vorher - zwischen - min) / raster) * raster;
    const gerastert = Math.round((position - vorher) / raster) * raster;
    const neu = werte.slice();
    neu[index] = Math.max(min, Math.min(maxBreite, gerastert));
    neu[n - 1] = runde2(gesamt - summe(neu.slice(0, n - 1)));
    return neu;
}
/**
 * Segmente einer Balkenzeile, wie der Client sie kennt: Feldwerte vor Serverwerten, das letzte Segment ist der Rest.
 */
export function segmentWerte(zeile, werte) {
    const server = zeile.segmente.map((s) => s.wert);
    const b = zeile.bearbeitbar;
    if (!b || b.felder.length !== server.length - 1 || b.feldFaktor <= 0)
        return server;
    const neu = server.map((wert, i) => {
        const feldWert = i < b.felder.length ? werte[b.felder[i]] : undefined;
        return typeof feldWert === "number" && Number.isFinite(feldWert) ? feldWert / b.feldFaktor : wert;
    });
    neu[neu.length - 1] = runde2(summe(server) - summe(neu.slice(0, -1)));
    return neu;
}
//# sourceMappingURL=konfiguratorAnzeige.js.map